import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Toast } from './Toast'
import { ToastContext, type AppToast, type NewToast, useToast } from './toastContext'

const createToastId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
    const [toasts, setToasts] = useState<AppToast[]>([])
    const hideTimeoutIdsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
    const removeTimeoutIdsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
    const closeDurationMs = 200

    const clearTimeoutById = (collection: Record<string, ReturnType<typeof setTimeout>>, toastId: string) => {
        if (collection[toastId]) {
            clearTimeout(collection[toastId])
            delete collection[toastId]
        }
    }

    const removeToast = useCallback((toastId: string) => {
        setToasts(prevToasts => prevToasts.filter(toast => toast.id !== toastId))
        clearTimeoutById(hideTimeoutIdsRef.current, toastId)
        clearTimeoutById(removeTimeoutIdsRef.current, toastId)
    }, [])

    const startClosingToast = useCallback(
        (toastId: string) => {
            setToasts(prevToasts =>
                prevToasts.map(toast =>
                    toast.id === toastId && !toast.isClosing ? { ...toast, isClosing: true } : toast
                )
            )

            clearTimeoutById(hideTimeoutIdsRef.current, toastId)
            clearTimeoutById(removeTimeoutIdsRef.current, toastId)

            removeTimeoutIdsRef.current[toastId] = setTimeout(() => {
                removeToast(toastId)
            }, closeDurationMs)
        },
        [removeToast]
    )

    const scheduleHide = useCallback(
        (toastId: string, delayMs: number) => {
            if (delayMs <= 0) {
                startClosingToast(toastId)
                return
            }

            clearTimeoutById(hideTimeoutIdsRef.current, toastId)
            hideTimeoutIdsRef.current[toastId] = setTimeout(() => {
                startClosingToast(toastId)
            }, delayMs)
        },
        [startClosingToast]
    )

    const showToast = useCallback(
        (toast: NewToast) => {
            const toastId = createToastId()
            const durationMs = toast.durationMs ?? 5000
            const now = Date.now()

            setToasts(prevToasts => [
                ...prevToasts,
                { ...toast, id: toastId, durationMs, remainingMs: durationMs, lastStartedAt: now, isClosing: false },
            ])

            if (durationMs > 0) {
                scheduleHide(toastId, durationMs)
            }
        },
        [scheduleHide]
    )

    const pauseToasts = useCallback(() => {
        const now = Date.now()
        setToasts(prevToasts =>
            prevToasts.map(toast => {
                if (toast.isClosing || toast.remainingMs <= 0 || !toast.lastStartedAt) {
                    return toast
                }

                const elapsedMs = now - toast.lastStartedAt
                const remainingMs = Math.max(0, toast.remainingMs - elapsedMs)
                clearTimeoutById(hideTimeoutIdsRef.current, toast.id)

                return {
                    ...toast,
                    remainingMs,
                    lastStartedAt: undefined,
                }
            })
        )
    }, [])

    const resumeToasts = useCallback(() => {
        const now = Date.now()
        setToasts(prevToasts =>
            prevToasts.map(toast => {
                if (toast.isClosing || toast.remainingMs <= 0 || toast.lastStartedAt) {
                    return toast
                }

                scheduleHide(toast.id, toast.remainingMs)

                return {
                    ...toast,
                    lastStartedAt: now,
                }
            })
        )
    }, [scheduleHide])

    useEffect(() => {
        return () => {
            Object.values(hideTimeoutIdsRef.current).forEach(timeoutId => clearTimeout(timeoutId))
            Object.values(removeTimeoutIdsRef.current).forEach(timeoutId => clearTimeout(timeoutId))
            hideTimeoutIdsRef.current = {}
            removeTimeoutIdsRef.current = {}
        }
    }, [])

    const value = useMemo(
        () => ({
            toasts,
            showToast,
            removeToast,
            pauseToasts,
            resumeToasts,
        }),
        [toasts, showToast, removeToast, pauseToasts, resumeToasts]
    )

    return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}

export const ToastViewport = () => {
    const { toasts, pauseToasts, resumeToasts } = useToast()

    // if (toasts.length === 0) {
    //     return null
    // }

    return (
        <div
            className='toast toast-top toast-end top-[60px] z-1000'
            role='status'
            aria-live='polite'
            onMouseEnter={pauseToasts}
            onMouseLeave={resumeToasts}
            onFocusCapture={pauseToasts}
            onBlurCapture={resumeToasts}
        >
            <div className='flex flex-col gap-2'>
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        className={`transition-all duration-200 ease-in ${
                            toast.isClosing ? '-translate-y-3 opacity-0' : 'translate-y-0 opacity-100'
                        }`}
                    >
                        <Toast type={toast.type}>{toast.message}</Toast>
                    </div>
                ))}
            </div>
        </div>
    )
}
